import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { RoutesPaths } from 'routes/Routes.types';

// base URL for reporting server
const reportingBaseUrl = `${process.env.REACT_APP_REPORTING_HOST}`;

// report dropdown item type
export type ReportDropDownItem = {
  created_date: string;
  default: boolean;
  description: string;
  filename: string;
  title: string;
  type: string;
};

// types for your reporting data
export interface ReportChartData {
  id: string;
  name: string;
  path: string;
  type: string;
  hidden?: boolean;
}

export interface ReportMetadata {
  title: string;
  type: string;
  name: string;
  dropdown_items?: ReportDropDownItem[];
}

export interface BannerReportData {
  metric: string;
  period: string;
  value: number;
  hidden?: boolean;
}

export interface ReportFileDownload {
  name: string;
  title: string;
  dropdown_items: {
    business_segment: string;
    subcategory: string;
    title: string;
    description: string;
    filename: string;
    created_date: string;
    type: string;
    label: string;
  }[];
  type: string;
}

// Create the reporting API slice
export const reportingApi = createApi({
  reducerPath: 'reportingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: reportingBaseUrl,
    prepareHeaders: async headers => {
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) {
          throw new Error('No active session');
        }
        headers.set('authorization', `Bearer ${idToken}`);
      } catch (err) {
        window.location.replace(`/${RoutesPaths.LOGIN}`);
        localStorage.clear();
      }
      return headers;
    },
  }),
  endpoints: builder => ({
    getCharts: builder.query<ReportChartData[], void>({
      query: () => '/reports/charts',
    }),
    getChartMetadata: builder.query<ReportMetadata, string>({
      query: chartId => `/reports/charts/${chartId}/metadata`,
    }),
    getChartSvg: builder.query<string, { path: string }>({
      query: ({ path }) => ({
        url: `/reports/charts/${path}`,
        responseHandler: response => response.text(),
      }),
    }),
    getBannerReport: builder.query<BannerReportData[], void>({
      query: () => '/reports/banner_report',
    }),
    getReportMetadata: builder.query<ReportFileDownload, void>({
      query: () => `/reports/reports/metadata`,
    }),
    downloadReport: builder.query<Blob, string>({
      query: filename => ({
        url: `/reports/reports/${filename}`,
        responseHandler: response => response.blob(),
      }),
    }),
  }),
});

// Export the auto-generated hooks
export const {
  useGetChartsQuery,
  useGetChartMetadataQuery,
  useGetChartSvgQuery,
  useGetBannerReportQuery,
  useDownloadReportQuery,
  useGetReportMetadataQuery,
  useLazyDownloadReportQuery,
} = reportingApi;
